import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { MarketingNav } from "@/components/layout/MarketingNav";
import {
  Network,
  Building2,
  CheckCircle2,
  Users,
  FileText,
  GitBranch,
  ChevronRight,
  ArrowRight,
  ClipboardList,
  Vote,
  Calendar,
  Zap,
} from "lucide-react";

const C = {
  navy: "#07081a",
  navyMid: "#0d0f2d",
  purple: "#6C63FF",
  lime: "#a8e063",
  white: "#ffffff",
  slate: "#c4c9e8",
  dimSlate: "#7880a8",
  border: "rgba(108,99,255,0.2)",
  glassBg: "rgba(11,13,40,0.7)",
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

// ─── Hero Snapshot ─────────────────────────────────────────────────────────────
const GovernanceHeroSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <Network size={14} style={{ color: C.purple }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Governance</span>
      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(108,99,255,0.15)", color: C.purple }}>3 Committees</span>
    </div>
    <div className="p-3 space-y-2">
      {/* Workflow pipeline */}
      <div className="flex items-center gap-1 text-[9px] mb-1">
        {["Draft", "Review", "Approval", "Approved"].map((s, i, arr) => (
          <div key={s} className="flex items-center gap-1">
            <div className="px-2 py-1 rounded font-medium" style={{ background: i <= 2 ? "rgba(108,99,255,0.2)" : `${C.lime}20`, color: i <= 2 ? C.purple : C.lime, border: `1px solid ${i <= 2 ? C.purple : C.lime}40` }}>{s}</div>
            {i < arr.length - 1 && <ChevronRight size={9} style={{ color: C.dimSlate }} />}
          </div>
        ))}
      </div>
      {/* Committees */}
      <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
        <div className="text-[10px] mb-2 font-semibold" style={{ color: C.dimSlate }}>Committees</div>
        {[
          { name: "Board Audit Committee", members: 4, status: "Active", color: C.lime },
          { name: "Risk & Compliance", members: 6, status: "Active", color: C.lime },
          { name: "IT Governance", members: 3, status: "Review", color: "#f59e0b" },
        ].map((c, i) => (
          <div key={i} className="flex items-center gap-2 py-1.5">
            <Building2 size={10} style={{ color: C.purple }} />
            <span className="text-[10px] flex-1" style={{ color: C.slate }}>{c.name}</span>
            <span className="text-[9px]" style={{ color: C.dimSlate }}>{c.members} members</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${c.color}18`, color: c.color }}>{c.status}</span>
          </div>
        ))}
      </div>
      {/* Voting result */}
      <div className="rounded-lg p-2.5" style={{ background: `${C.lime}06`, border: `1px solid ${C.lime}20` }}>
        <div className="flex items-center gap-2">
          <CheckCircle2 size={12} style={{ color: C.lime }} />
          <div>
            <div className="text-[10px] font-semibold" style={{ color: C.white }}>Policy Approved — Vote 5/5</div>
            <div className="text-[9px]" style={{ color: C.dimSlate }}>Information Security Policy · 2m ago</div>
          </div>
        </div>
      </div>
      {/* ToR item */}
      <div className="rounded-lg p-2.5" style={{ background: "rgba(108,99,255,0.06)", border: `1px solid rgba(108,99,255,0.2)` }}>
        <div className="flex items-center gap-2 mb-1">
          <FileText size={10} style={{ color: C.purple }} />
          <span className="text-[10px] font-semibold" style={{ color: C.slate }}>Terms of Reference</span>
        </div>
        {["Board Audit ToR v2", "Risk Committee ToR"].map((t, i) => (
          <div key={i} className="flex items-center gap-1 py-0.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: C.purple }} />
            <span className="text-[9px]" style={{ color: C.dimSlate }}>{t}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Feature Cards ─────────────────────────────────────────────────────────────
const features = [
  { icon: <Network size={20} />, color: C.purple, title: "Org Chart Management", desc: "Define your organisation's reporting hierarchy visually. Assign roles, departments, and accountability chains." },
  { icon: <Building2 size={20} />, color: "#a78bfa", title: "Committee Tracking", desc: "Manage board and sub-committees with full member rosters, mandates, and meeting history." },
  { icon: <FileText size={20} />, color: C.lime, title: "Terms of Reference Editor", desc: "Draft, review, approve, and store ToRs for every committee — with a full version history." },
  { icon: <Vote size={20} />, color: "#f59e0b", title: "Electronic Voting", desc: "Record all governance decisions with a traceable audit log. Vote outcomes are logged and stored permanently." },
  { icon: <Calendar size={20} />, color: C.purple, title: "Meeting Minutes", desc: "Capture, store, and retrieve meeting minutes efficiently. Every decision and action item is documented." },
  { icon: <Zap size={20} />, color: C.lime, title: "Task Automation", desc: "Assign governance tasks automatically based on committee structures and responsibility chains." },
];

const steps = [
  { num: "01", title: "Structure", desc: "Build your org chart, define committees, and assign roles with clear accountability at every level." },
  { num: "02", title: "Delegate", desc: "Draft Terms of Reference, set committee mandates, and assign owners to governance tasks." },
  { num: "03", title: "Govern", desc: "Run electronic votes, capture meeting minutes, and maintain a permanent audit trail of all decisions." },
];

const stats = [
  { value: "100%", label: "Decision audit trail" },
  { value: "ToR", label: "Editor built-in" },
  { value: "5/5", label: "Vote traceability" },
  { value: "0", label: "Decisions lost" },
];

export default function GovernanceFeature() {
  return (
    <div style={{ background: C.navy, minHeight: "100vh", fontFamily: "'DM Sans', ui-sans-serif, sans-serif", color: C.white }}>
      <MarketingNav />

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-32 pb-24 px-6 md:px-16 lg:px-24">
        <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 50% at 20% 40%, rgba(108,99,255,0.08) 0%, transparent 70%)` }} />

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
              style={{ background: "rgba(108,99,255,0.12)", border: "1px solid rgba(108,99,255,0.25)", color: C.purple }}>
              <Network size={13} /> Governance Module
            </motion.div>
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="text-4xl md:text-5xl font-bold leading-[1.15] mb-5"
              style={{ letterSpacing: "-0.03em" }}>
              Structure Authority.<br />
              <span style={{ color: C.purple }}>Drive Accountability.</span>
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="text-base mb-8 max-w-md leading-relaxed" style={{ color: C.slate }}>
              Org chart management, committee tracking, electronic voting, and task automation — the full governance layer that regulated organisations need to operate with clarity.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="flex flex-wrap gap-3">
              <NavLink to="/auth"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: C.purple, color: C.white }}>
                Start Free Trial <ArrowRight size={15} />
              </NavLink>
              <NavLink to="/consulting"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                style={{ border: `1px solid ${C.border}`, color: C.slate }}>
                Request a Demo
              </NavLink>
            </motion.div>
          </div>

          {/* Right — mock UI */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2}>
            <GovernanceHeroSnapshot />
          </motion.div>
        </div>
      </section>

      {/* ── Stats Bar ────────────────────────────────────────────────────────── */}
      <section className="py-10 px-6 md:px-16 lg:px-24" style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="text-center">
              <div className="text-3xl font-bold mb-1" style={{ color: C.purple }}>{s.value}</div>
              <div className="text-sm" style={{ color: C.dimSlate }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Key Features ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ letterSpacing: "-0.025em" }}>
              The full governance layer
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: C.slate }}>
              From committee setup to voted decisions — everything you need to govern with structure and accountability.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}
                className="rounded-xl p-6"
                style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${C.border}` }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${f.color}16`, color: f.color }}>
                  {f.icon}
                </div>
                <h3 className="text-base font-semibold mb-2" style={{ color: C.white }}>{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24" style={{ background: "rgba(255,255,255,0.015)" }}>
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ letterSpacing: "-0.025em" }}>How it works</h2>
            <p className="text-base" style={{ color: C.slate }}>Three steps to governance clarity across your organisation.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="relative">
                <div className="text-6xl font-black mb-4 leading-none" style={{ color: "rgba(108,99,255,0.12)" }}>{s.num}</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: C.white }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 -right-4 text-2xl" style={{ color: C.border }}>→</div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center rounded-2xl px-8 py-16"
          style={{ background: `linear-gradient(135deg, rgba(108,99,255,0.12) 0%, rgba(168,224,99,0.05) 100%)`, border: `1px solid ${C.border}` }}>
          <Network size={36} className="mx-auto mb-5" style={{ color: C.purple }} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ letterSpacing: "-0.025em" }}>
            Governance that actually works.
          </h2>
          <p className="text-base mb-8 max-w-md mx-auto" style={{ color: C.slate }}>
            Give your board and committees the structure they need to make decisions, delegate authority, and stay accountable.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <NavLink to="/auth"
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: C.purple, color: C.white }}>
              Start Free Trial <ArrowRight size={15} />
            </NavLink>
            <NavLink to="/consulting"
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all hover:opacity-80"
              style={{ border: `1px solid ${C.border}`, color: C.slate }}>
              Request a Demo
            </NavLink>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
